import { Grid } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Note } from 'sr-types/lib/search/v1/graphql';
import { useIsWithinBreakpoints } from '../../common/hooks/useIsWithinBreakpoints';
import { useGetNote } from '../../common/notes';
import { useDataListCell } from '../DataList/DataListCellContext';
import { ProductionTypeSuperset } from '../helpers/productionApi';
import ProductionInfoAndOwners from './ProductionInfoAndOwners';
import ProductionStatusNote from './statusNote/ProductionStatusNote';

type ProductionMetadataGridProps = {
    workspace: ProductionTypeSuperset;
    setWorkspace?: Dispatch<SetStateAction<ProductionTypeSuperset>>;
    statusNote?: {
        enableCurrentNote?: boolean;
        onCurrentNoteClick?: (row: ProductionTypeSuperset, panelId: string, note: Note) => void;
        onStatusNoteDeleted?: (noteId: string, productionId: string) => void;
        onStatusNoteChanged?: (note: Note | null, productionId: string) => void;
    };
    isReadOnly?: boolean;
    isSaving?: boolean;
    sidePanelView?: boolean;
};

export const ProductionMetadataGrid = ({
    workspace,
    setWorkspace,
    statusNote,
    isReadOnly,
    isSaving,
    sidePanelView = false
}: ProductionMetadataGridProps) => {
    // if the note is in edit mode, expand the note section
    const [expandNoteSection, setExpandNoteSection] = useState<boolean>(false);
    const [fetchedNote, setFetchedNote] = useState<Note | undefined>(undefined);
    const { isNotesPanelOpen } = useDataListCell();
    const isBelowLargeBreakpoint = useIsWithinBreakpoints(['xs', 's', 'm']);

    const noteId = workspace.currentStatusNoteReference?.id;

    const skipFetchingNote =
        !noteId ||
        Boolean(workspace.currentStatusNote) ||
        workspace.currentStatusNoteReference?.deleted ||
        workspace.currentStatusNote === null;

    const { noteDetails, isNoteLoading } = useGetNote(noteId || '', skipFetchingNote);

    useEffect(() => {
        if (noteDetails?.note) {
            const transformedNote = {
                ...noteDetails.note,
                id: (noteDetails.note as unknown as Note).id || noteDetails.note.reference?.id
            };
            setFetchedNote(transformedNote as unknown as Note);
        }
    }, [noteDetails]);

    useEffect(() => {
        setFetchedNote(undefined);
    }, [workspace.currentStatusNoteReference?.id]);

    const currentStatusNote = workspace.currentStatusNote || fetchedNote;

    // Apply mobile/tablet styling when viewport is < 1200px AND notes panel is open
    const shouldUseMobileStyling = isNotesPanelOpen && isBelowLargeBreakpoint;

    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: shouldUseMobileStyling || sidePanelView ? 12 : expandNoteSection ? 4 : 8 }}>
                <ProductionInfoAndOwners
                    workspace={workspace}
                    setWorkspace={setWorkspace}
                    isReadOnly={isReadOnly}
                    isSaving={isSaving}
                />
            </Grid>
            <Grid size={{ xs: 12, md: shouldUseMobileStyling || sidePanelView ? 12 : expandNoteSection ? 8 : 4 }}>
                <ProductionStatusNote
                    workspace={workspace}
                    currentStatusNoteReference={workspace.currentStatusNoteReference}
                    currentStatusNote={currentStatusNote}
                    statusNote={statusNote}
                    setExpandNoteSection={setExpandNoteSection}
                    isLoading={isNoteLoading}
                />
            </Grid>
        </Grid>
    );
};

export default ProductionMetadataGrid;
